import { createSelector } from '@reduxjs/toolkit';
import { ContentModel } from '../../models/content.model';
import { BannerModalConfig, BannerModalPosition, GoogleMapsType, Setting, SettingKey, VolcanoBannerImageTextPosition, VolcanoBannerModalConfig } from '../../models/setting.model';

const selectPublicState = (state: any) => state.public;

export const selectAvailability = createSelector(
  [selectPublicState],
  (publicState) => publicState?.availability || []
);

export const selectAvailabilityAndSettings = createSelector(
  [selectPublicState],
  (publicState) => ({
    availability: publicState?.availability || [],
    settings: publicState?.settings || []
  })
);

export const selectLoading = createSelector(
  [selectPublicState],
  (publicState) => publicState?.loading
);

export const selectBooking = createSelector(
  [selectPublicState],
  (publicState) => publicState?.booking
);

export const selectRedirectUrl = createSelector(
  [selectPublicState],
  (publicState) => publicState?.redirectUrl
);

export const selectFormData = createSelector(
  [selectPublicState],
  (publicState) => publicState?.form_data
)

export const selectContent = createSelector(
  [selectPublicState],
  (publicState) => publicState?.content
);

export const selectHeroContent = createSelector(
  [selectContent],
  (content: ContentModel) => ({
    hero: content?.hero,
    buttons: content?.buttons
  })
);

export const selectReservationContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.reservation
);

export const selectFooterContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.footer
);

export const selectMapContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.map
);

export const selectMoreInfoContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.moreInfo
);

export const selectPricingContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.pricing
);

export const selectLandingPageContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.main
);

export const selectSuccessContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.success
);

export const selectErrorContent = createSelector(
  [selectContent],
  (content: ContentModel) => content?.error
);

export const selectTheaterMapData = createSelector(
  [selectPublicState],
  (publicState) => ({
    googleMapsType: publicState?.settings?.find((setting: Setting) => setting.setting_key === SettingKey.GOOGLE_MAPS_TYPE)?.setting_value as GoogleMapsType || GoogleMapsType.IFRAME,
    mapContent: publicState?.content?.map,
  }));

export const selectBookingCreateLoading = createSelector(
  [selectPublicState],
  (publicState) => publicState?.bookingCreateLoading
);

export const selectEmailLoading = createSelector(
  [selectPublicState],
  (publicState) => publicState?.emailLoading
);

export const selectVipImageUrl = createSelector(
  [selectPublicState],
  (publicState) => publicState?.settings?.find((setting: Setting) => setting.setting_key === SettingKey.VIP_IMAGE_URL)?.setting_value as string || '/assets/images/golden_ticket.jpg'
);

export const selectShowGroupDealsBanner = createSelector(
  [selectPublicState],
  (publicState) => publicState?.settings?.find((setting: Setting) => setting.setting_key === SettingKey.SHOW_GROUP_DEALS_BANNER)?.setting_value as boolean || false
);

export const DEFAULT_BANNER_MODAL_CONFIG: BannerModalConfig = {
  enabled: false,
  title: '',
  text: '',
  backgroundColor: '#243c6f',
  textColor: '#ffd700',
  borderColor: '#ffd700',
  fontSize: 16,
  position: BannerModalPosition.BOTTOM_RIGHT,
};

export const DEFAULT_VOLCANO_BANNER_MODAL_CONFIG: VolcanoBannerModalConfig = {
  enabled: false,
  title: 'The Greek Wedding Show + Volcano Day',
  text: '<p>Start your night at 7:00 PM with a rooftop pre\u2011party: a welcome glass of sparkling wine, snacks, live bouzouki, and sunset colors. Then enjoy the best view as fireworks light up Santorini\u2019s caldera during the spectacular Ifestia (Volcano) Festival. Head straight to The Greek Wedding Show!</p><p>Price: €130 (all-inclusive)</p><p>Your ticket includes:</p><ul><li>Rooftop pre-party with a welcome glass of sparkling wine, snacks, live music, and sunset views</li><li>The best view of the Ifestia (Volcano) Festival fireworks over Santorini\u2019s caldera</li><li>Admission to the immersive Greek Wedding Show</li></ul>',
  imageUrl: '/assets/images/volcano_day_banner.png',
  showImageText: true,
  imageText: 'SPECIAL EVENT!',
  imageTextPosition: VolcanoBannerImageTextPosition.BOTTOM,
  backgroundColor: '#0094c9',
  textColor: '#fed537',
  borderColor: '#fed537',
  fontSize: 16,
  position: BannerModalPosition.BOTTOM_LEFT,
};

export const selectBannerModalSettings = createSelector(
  [selectPublicState],
  (publicState): BannerModalConfig => {
    const settings: Setting[] = publicState?.settings || [];
    const config = settings.find((setting: Setting) => setting.setting_key === SettingKey.BANNER_MODAL_CONFIG)?.setting_value as Partial<BannerModalConfig> | undefined;
    return { ...DEFAULT_BANNER_MODAL_CONFIG, ...(config || {}) };
  }
);

export const selectVolcanoBannerModalSettings = createSelector(
  [selectPublicState],
  (publicState): VolcanoBannerModalConfig => {
    const settings: Setting[] = publicState?.settings || [];
    const config = settings.find((setting: Setting) => setting.setting_key === SettingKey.VOLCANO_BANNER_MODAL_CONFIG)?.setting_value as Partial<VolcanoBannerModalConfig> | undefined;
    return { ...DEFAULT_VOLCANO_BANNER_MODAL_CONFIG, ...(config || {}) };
  }
);

export const selectPromoCodeValidation = createSelector(
  [selectPublicState],
  (publicState) => publicState?.promoCodeValidation
);

export const selectPromoCodeLoading = createSelector(
  [selectPublicState],
  (publicState) => publicState?.promoCodeLoading
);